'use client'

import { m, useReducedMotion, Variants } from 'framer-motion'
import { AnchorLink } from '@/ui/components/ui/anchor-link'
import { useRef } from 'react'

import { MenuVariant } from '@/types/menu.types'
import { menuConfig } from '@/ui/components/navbar/config'

interface MobileMenuProps {
  onToggle: () => void
  variant: MenuVariant
}

export function MobileMenu({ onToggle, variant }: MobileMenuProps) {
  const config = menuConfig[variant]
  const shouldReduceMotion = useReducedMotion()
  const touchStartX = useRef<number | null>(null)

  if (!config) return null

  const panel: Variants = {
    enter: { x: 0, opacity: 1, transition: { duration: 0.35, ease: 'easeOut' } },
    leave: {
      x: shouldReduceMotion ? 0 : '100%',
      opacity: shouldReduceMotion ? 0 : 1,
      transition: { duration: 0.28, ease: 'easeIn' },
    },
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const distance = e.changedTouches[0].clientX - touchStartX.current
    if (distance > 50) onToggle()
    touchStartX.current = null
  }

  return (
    <m.div
      initial="leave"
      animate="enter"
      exit="leave"
      variants={panel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="on-dark fixed inset-0 top-20 z-50 flex h-[calc(100svh-5rem)] flex-col bg-ink desktop:hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 grid-paper text-chalk opacity-30"
      />

      <nav
        aria-label="Menu principal"
        className="relative flex-1 overflow-y-auto px-6 pt-8"
      >
        <ul>
          {config.links.map((link) => (
            <li key={link.href} className="border-b border-chalk/10">
              <AnchorLink
                aria-label={link.ariaLabel}
                href={link.href}
                title={link.title}
                className="block py-5 font-display text-3xl font-bold tracking-tight text-chalk"
                onClick={onToggle}
              >
                {link.label}
              </AnchorLink>
            </li>
          ))}
        </ul>
      </nav>

      {config.showCta && (
        <div className="relative border-t border-chalk/15 px-6 py-6">
          <a
            href="tel:+33620111014"
            aria-label="Appeler le 06 20 11 10 14"
            title="appelez-nous"
            className="flex flex-col gap-2"
          >
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-mist">
              Appelez-nous directement
            </span>
            <span
              data-figure
              className="font-display text-3xl font-bold tracking-tight text-signal"
            >
              06 20 11 10 14
            </span>
          </a>
        </div>
      )}
    </m.div>
  )
}
